// import hooks
import { useGetAdminFunctions } from 'queries';
import { isNil, pipe } from 'ramda';
import { useTranslation } from 'react-i18next';
import useColumnDefinitions from './hooks/useColumnDefinitions';
import { useCallback, useState } from 'react';

// import api's and mutations
import { ADMIN_SETTINGS_FUNCTIONS } from 'RestAPIs/endpoints';
import { useDeleteById } from 'RestAPIs/useDeleteById';

// import components
import { LoadingOverlay } from 'components/shared';
import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';
import CreateEditFunctionForm from './components/form/CreateEditFunctionForm';
import { FilterComponent } from './filter/FilterComponent';
import { Filters } from 'components/admin-portal/Tables/Settings/AdminFunctions/filter/FilterComponent.types';

// import types
import { AdminFunction } from './AdminFunctions.types';

const Functions = () => {
  const { t } = useTranslation();

  const { data: rows, isFetching, isError, refetch } = useGetAdminFunctions();
  const [filteredRows, setFilteredRows] = useState<
    AdminFunction[] | undefined
  >();
  const columnDefinitions = useColumnDefinitions();
  const { mutate: handleDelete } = useDeleteById(ADMIN_SETTINGS_FUNCTIONS, [
    'adminFunctions',
    'list'
  ]);

  const filterBySearch = (data: AdminFunction[], search: string | null) => {
    if (isNil(search)) return data;
    return data.filter(
      (item) =>
        item.code?.toLowerCase().includes(search.toLowerCase()) ||
        item.description?.toLowerCase().includes(search.toLowerCase())
    );
  };

  const handleFilterChange = useCallback(
    async (filters: Filters) => {
      if (isFetching) return rows;
      const { search } = filters;

      const filtered = await pipe((data) => filterBySearch(data, search))(rows);

      setFilteredRows(filtered);
    },
    [rows]
  );

  return (
    <>
      <FilterComponent
        handleFilterChange={handleFilterChange}
        dependencies={[rows]}
      />
      <TableCRUDLayout
        FormComponent={CreateEditFunctionForm}
        rows={filteredRows}
        loading={isFetching}
        hasLoadingError={isError}
        onLoadingError={refetch}
        columns={columnDefinitions}
        getRowId={(item) => item.functionId}
        dynamicHeight={false}
        confirmationTitle={t('functions.deleteDialog.confirmationTitle')}
        confirmationBody={t('functions.deleteDialog.confirmationBody')}
        onDeleteClick={(id) => {
          handleDelete(id);
        }}
        initialState={{
          sorting: {
            sortModel: [{ field: 'code', sort: 'asc' }]
          }
        }}
      />
      <LoadingOverlay loading={isNil(rows)} />
    </>
  );
};

export default Functions;
